// communityProfile/container.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Import view component
import CommunityProfileView from './view';
import CommunityReportDialog from './communityReportDialog';

// Import API functions
import { getCommunity, updateMembershipProfile, leaveCommunity, reportCommunity } from 'api';

const CommunityProfileContainer = ({
  slug, // Community slug
  membershipSlug, // User's membership slug
}) => {
  const navigate = useNavigate();
  
  // State management
  const [membership, setMembership] = useState(null);
  const [community, setCommunity] = useState(null);
  const [formData, setFormData] = useState({
    visibility: 'GR', // Default visibility
  });
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showReportDialog, setShowReportDialog] = useState(false);

  // Fetch membership and community data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (slug) {
          // Fetch community info (this should include the user's membership data)
          const communityResponse = await getCommunity(slug);
          
          // Extract community and membership data
          const communityData = communityResponse.group || communityResponse.data?.group;
          const membershipData = communityResponse.my_membership || communityResponse.data?.my_membership;
          
          if (!membershipData) {
            setErrorMessage('You are not a member of this community.');
            return;
          }
          
          setCommunity(communityData);
          setMembership(membershipData);
          
          // Initialize form data with current membership settings
          setFormData({
            visibility: membershipData?.visibility || 'GR',
          });
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        
        // Handle different error types
        if (error.response?.status === 404) {
          setErrorMessage('Community not found.');
        } else if (error.response?.status === 403) {
          setErrorMessage('You do not have access to this community.');
        } else {
          setErrorMessage('Failed to load profile data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug, membershipSlug]);

  // Event handlers
  const handleVisibilityChange = (visibility) => {
    setFormData(prev => ({
      ...prev,
      visibility: visibility
    }));
  };

  const handleSave = async () => {
    if (!membership?.slug) {
      setErrorMessage('Membership information not available.');
      return;
    }

    try {
      setSaveLoading(true);

      // Prepare data for API
      const updateData = {
        visibility: formData.visibility,
      };

      // Call API to update membership using the new endpoint structure
      const response = await updateMembershipProfile(membership.slug, updateData);
      
      if (response && (response.success || response.status === 200)) {
        setSuccessMessage(response.message || 'Profile settings updated successfully!');
        
        // Update local membership state with the returned data
        if (response.membership) {
          setMembership(prev => ({
            ...prev,
            ...response.membership
          }));
        } else {
          // Fallback to updating with our local data
          setMembership(prev => ({
            ...prev,
            visibility: updateData.visibility
          }));
        }
        
      } else {
        throw new Error(response?.error || response?.message || 'Failed to update profile settings');
      }

    } catch (error) {
      console.error('Error saving profile:', error);
      
      // Handle different error types based on the backend response
      if (error.response?.status === 400) {
        setErrorMessage(error.response.data?.error || 'Invalid data provided. Please check your settings.');
      } else if (error.response?.status === 403) {
        setErrorMessage('You do not have permission to update this profile.');
      } else if (error.response?.status === 404) {
        setErrorMessage('Membership not found.');
      } else {
        setErrorMessage(error.message || 'Failed to save changes. Please try again.');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!membership?.slug) {
      setErrorMessage('Membership information not available.');
      return;
    }

    // Check if user is founder
    if (membership.role === 'FO') {
      setErrorMessage('Founders cannot leave their own community. Please transfer ownership first.');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to leave ${community?.name}? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      
      // Call API to leave community using the new endpoint structure
      const response = await leaveCommunity(membership.slug);
      
      if (response && (response.success || response.status === 200)) {
        setSuccessMessage(response.message || 'You have successfully left the community.');
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/dashboard'); // or wherever you want to redirect
        }, 2000);
        
      } else {
        throw new Error(response?.error || response?.message || 'Failed to leave community');
      }

    } catch (error) {
      console.error('Error leaving community:', error);
      
      // Handle different error types
      if (error.response?.status === 400 && error.response.data?.code === 'FOUNDER_CANNOT_LEAVE') {
        setErrorMessage('Founders cannot leave their own community. Please transfer ownership first.');
      } else if (error.response?.status === 403) {
        setErrorMessage('You do not have permission to leave this community.');
      } else if (error.response?.status === 404) {
        setErrorMessage('Membership not found.');
      } else {
        setErrorMessage(error.message || 'Failed to leave community. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReportGroup = () => {
    if (!community?.slug) {
      setErrorMessage('Community information not available.');
      return;
    }

    // Show the report dialog
    setShowReportDialog(true);
  };

  const handleReportSubmit = async (reportData) => {
    try {
      setSaveLoading(true);
      
      // Call API to report community using the community slug
      const response = await reportCommunity(community.slug, {
        reason: reportData.reason,
        description: reportData.description,
        report_type: reportData.report_type
      });
      
      if (response && (response.success || response.status === 200)) {
        setSuccessMessage(response.message || 'Thank you for your report. We will review it shortly.');
        setShowReportDialog(false);
      } else {
        throw new Error(response?.error || response?.message || 'Failed to submit report');
      }

    } catch (error) {
      console.error('Error reporting community:', error);
      
      // Handle different error types
      if (error.response?.status === 403) {
        setErrorMessage('You must be a member to report this community.');
      } else if (error.response?.status === 404) {
        setErrorMessage('Community not found.');
      } else {
        setErrorMessage(error.message || 'Failed to submit report. Please try again.');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleReportDialogClose = () => {
    setShowReportDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  return (
    <>
      <CommunityProfileView
        // Data props
        membership={membership}
        community={community}
        formData={formData}
        loading={loading}
        saveLoading={saveLoading}
        successMessage={successMessage}
        errorMessage={errorMessage}
        
        // Handler props
        onVisibilityChange={handleVisibilityChange}
        onSave={handleSave}
        onLeaveGroup={handleLeaveGroup}
        onReportGroup={handleReportGroup}
        onCloseSnackbar={handleCloseSnackbar}
      />

      <CommunityReportDialog
        open={showReportDialog}
        onClose={handleReportDialogClose}
        onSubmit={handleReportSubmit}
        communityName={community?.name}
        loading={saveLoading}
      />
    </>
  );
};

export default CommunityProfileContainer;